import React, { useState } from 'react';
import {
  ChevronDown, ChevronRight, Clock, Package, ClipboardList, Zap,
  CheckCircle, ArrowDownUp, FileText, Upload, Info, AlertTriangle,
  AlertCircle, XCircle, Filter
} from 'lucide-react';
import { OrderTimelineEventDto } from '../../../../../types/orderTimeline';

interface TimelineEventListProps {
  events: OrderTimelineEventDto[];
}

const phaseLabels: Record<string, string> = {
  order: 'Ordre',
  picking: 'Plukk',
  stock: 'Lager',
  export: 'Eksport',
  log: 'Logg'
};

const phaseColors: Record<string, string> = {
  order: 'bg-blue-100 text-blue-700',
  picking: 'bg-green-100 text-green-700',
  stock: 'bg-purple-100 text-purple-700',
  export: 'bg-orange-100 text-orange-700',
  log: 'bg-gray-100 text-gray-700'
};

const severityIcons: Record<string, React.FC<{ className?: string }>> = {
  info: Info,
  success: CheckCircle,
  warning: AlertTriangle,
  error: XCircle
};

const severityColors: Record<string, string> = {
  info: 'text-blue-500',
  success: 'text-green-500',
  warning: 'text-yellow-500',
  error: 'text-red-500'
};

const severityLineColors: Record<string, string> = {
  info: 'border-blue-200',
  success: 'border-green-200',
  warning: 'border-yellow-200',
  error: 'border-red-200'
};

const formatDateTime = (dateStr: string) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' +
    d.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

export const TimelineEventList: React.FC<TimelineEventListProps> = ({ events }) => {
  const [expanded, setExpanded] = useState(true);
  const [filterPhase, setFilterPhase] = useState<string | null>(null);
  const [filterSeverity, setFilterSeverity] = useState<string | null>(null);

  // Unique phases present in events
  const phases = Array.from(new Set(events.map(e => e.phase)));

  const filteredEvents = events.filter(e => {
    if (filterPhase && e.phase !== filterPhase) return false;
    if (filterSeverity && e.severity !== filterSeverity) return false;
    return true;
  });

  return (
    <div className="mb-4">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700 hover:text-gray-900"
      >
        {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <Clock className="w-4 h-4" />
        Tidslinje ({events.length} hendelser)
      </button>

      {expanded && (
        <>
          {/* Filters */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Filter className="w-3.5 h-3.5 text-gray-400" />
            <button
              onClick={() => { setFilterPhase(null); setFilterSeverity(null); }}
              className={`px-2 py-0.5 text-xs rounded-full border transition-colors ${
                !filterPhase && !filterSeverity ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Alle
            </button>
            {phases.map(phase => (
              <button
                key={phase}
                onClick={() => setFilterPhase(filterPhase === phase ? null : phase)}
                className={`px-2 py-0.5 text-xs rounded-full border transition-colors ${
                  filterPhase === phase ? 'bg-gray-800 text-white border-gray-800' : `${phaseColors[phase]} border-transparent hover:opacity-80`
                }`}
              >
                {phaseLabels[phase] || phase}
              </button>
            ))}
            <span className="text-gray-300">|</span>
            {['error', 'warning', 'success', 'info'].map(sev => {
              const count = events.filter(e => e.severity === sev).length;
              if (count === 0) return null;
              return (
                <button
                  key={sev}
                  onClick={() => setFilterSeverity(filterSeverity === sev ? null : sev)}
                  className={`px-2 py-0.5 text-xs rounded-full border transition-colors ${
                    filterSeverity === sev ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {sev === 'error' ? 'Feil' : sev === 'warning' ? 'Advarsel' : sev === 'success' ? 'OK' : 'Info'} ({count})
                </button>
              );
            })}
          </div>

          {/* Event list */}
          <div className="relative pl-6 space-y-0">
            {filteredEvents.map((event, i) => {
              const SeverityIcon = severityIcons[event.severity] || Info;
              const isLast = i === filteredEvents.length - 1;
              return (
                <div key={i} className="relative pb-3">
                  {/* Vertical connector line */}
                  {!isLast && (
                    <div className={`absolute left-[-14px] top-5 bottom-0 w-px border-l-2 ${severityLineColors[event.severity] || 'border-gray-200'}`} />
                  )}
                  {/* Dot */}
                  <div className={`absolute left-[-18px] top-1 w-2 h-2 rounded-full ${
                    event.severity === 'error' ? 'bg-red-500' :
                    event.severity === 'warning' ? 'bg-yellow-500' :
                    event.severity === 'success' ? 'bg-green-500' :
                    'bg-blue-400'
                  }`} />

                  <div className="bg-white rounded-lg border border-gray-100 p-2.5 hover:border-gray-200 transition-colors">
                    <div className="flex items-start gap-2">
                      <SeverityIcon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${severityColors[event.severity]}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-gray-800">{event.description}</span>
                          <span className={`px-1.5 py-0 text-[10px] rounded-full ${phaseColors[event.phase]}`}>
                            {phaseLabels[event.phase] || event.phase}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400">
                          <span>{formatDateTime(event.timestamp)}</span>
                          {event.operatorID && <span>Operatør: {event.operatorID}</span>}
                        </div>
                        {event.details && (
                          <div className="mt-1 text-xs text-gray-500 bg-gray-50 p-1.5 rounded font-mono whitespace-pre-wrap">
                            {event.details}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {filteredEvents.length === 0 && (
            <div className="text-center text-sm text-gray-400 py-4">
              Ingen hendelser matcher filteret
            </div>
          )}
        </>
      )}
    </div>
  );
};
